import { Box, Typography, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DARK_THEME_PRESETS, DEFAULT_PURPLE_PRESET, DEFAULT_THEME_KEY } from "../../../config/theme.presets";

export default function ThemeSelector({
  title,
  icon: Icon,
  allItems,
  selected = [],
  setSelected,
}) {
  const theme = useTheme();
  const activeKey = selected[0] || null;

  const select = (key) => {
    setSelected([key]);
  };

  // Get the preset for the currently selected theme
  const activePreset =
    activeKey === DEFAULT_THEME_KEY
      ? DEFAULT_PURPLE_PRESET
      : DARK_THEME_PRESETS[activeKey] || DEFAULT_PURPLE_PRESET;

  const activeItem = allItems.find((i) => i.key === activeKey) || allItems[0];

  return (
    <Box sx={{ display: "flex", gap: 3 }}>
      {/* Left - Theme Cards Grid */}
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
          }}
        >
          {allItems.map((item) => {
            const active = activeKey === item.key;

            return (
              <Paper
                key={item.key}
                elevation={0}
                onClick={() => select(item.key)}
                sx={{
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  cursor: "pointer",
                  borderRadius: "10px",
                  border: active
                    ? `2px solid ${theme.palette.primary.main}`
                    : "1px solid #e2e8f0",
                  bgcolor: "#fff",
                  position: "relative",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: active
                      ? theme.palette.primary.main
                      : "#cbd5e1",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  },
                }}
              >
                {/* Theme icon dot */}
                <Box
                  sx={{
                    color: item.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>

                {/* Label */}
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: active ? 600 : 500,
                    color: "#1e293b",
                    flex: 1,
                  }}
                >
                  {item.label}
                </Typography>

                {/* Checkmark for active */}
                {active && (
                  <CheckCircleIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.primary.main,
                    }}
                  />
                )}
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* Right - Theme Live Preview */}
      <Box sx={{ width: 300, flexShrink: 0 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            bgcolor: "#fff",
            overflow: "hidden",
          }}
        >
          {/* Preview Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 2,
            }}
          >
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#1e293b" }}>
              Theme Live Preview
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#ef4444" }} />
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#f59e0b" }} />
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#22c55e" }} />
            </Box>
          </Box>

          {/* Dark Preview Area */}
          <Box
            sx={{
              mx: 2,
              mb: 2,
              borderRadius: "8px",
              overflow: "hidden",
              background: `linear-gradient(180deg, ${activePreset.sidebarBgStart}, ${activePreset.sidebarBgEnd})`,
              p: 2,
              minHeight: 160,
              display: "flex",
              gap: 1.5,
            }}
          >
            {/* Sidebar mockup */}
            <Box sx={{ width: 50, display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ width: "100%", height: 8, borderRadius: 4, bgcolor: activePreset.primary, opacity: 0.9 }} />
              <Box sx={{ width: "100%", height: 6, borderRadius: 4, bgcolor: "rgba(255,255,255,0.15)" }} />
              <Box sx={{ width: "100%", height: 6, borderRadius: 4, bgcolor: "rgba(255,255,255,0.15)" }} />
              <Box sx={{ width: "100%", height: 6, borderRadius: 4, bgcolor: "rgba(255,255,255,0.15)" }} />
              <Box sx={{ width: "100%", height: 6, borderRadius: 4, bgcolor: "rgba(255,255,255,0.15)" }} />
            </Box>

            {/* Main content mockup */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Header bar */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Box sx={{ width: 30, height: 6, borderRadius: 4, bgcolor: "rgba(255,255,255,0.2)" }} />
                  <Box sx={{ width: 30, height: 6, borderRadius: 4, bgcolor: "rgba(255,255,255,0.2)" }} />
                </Box>
                <Box sx={{ width: 40, height: 14, borderRadius: 7, bgcolor: activePreset.primary }} />
              </Box>

              {/* Card rows */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Box
                  sx={{
                    flex: 1,
                    p: 1,
                    borderRadius: "6px",
                    bgcolor: "rgba(255,255,255,0.06)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Box sx={{ width: "60%", height: 5, borderRadius: 4, bgcolor: activePreset.primary }} />
                  <Box sx={{ width: "80%", height: 4, borderRadius: 4, bgcolor: "rgba(255,255,255,0.15)" }} />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    p: 1,
                    borderRadius: "6px",
                    bgcolor: "rgba(255,255,255,0.06)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Box sx={{ width: "50%", height: 5, borderRadius: 4, bgcolor: activePreset.secondary || activePreset.primary }} />
                  <Box sx={{ width: "70%", height: 4, borderRadius: 4, bgcolor: "rgba(255,255,255,0.15)" }} />
                </Box>
              </Box>

              {/* Circle accent */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: activePreset.secondary || activePreset.primary, opacity: 0.8 }} />
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Box sx={{ width: "70%", height: 4, borderRadius: 4, bgcolor: "rgba(255,255,255,0.15)" }} />
                  <Box sx={{ width: "50%", height: 4, borderRadius: 4, bgcolor: "rgba(255,255,255,0.1)" }} />
                </Box>
                <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: activePreset.primary }} />
              </Box>

              {/* Bottom bars */}
              <Box
                sx={{
                  p: 1,
                  borderRadius: "6px",
                  bgcolor: "rgba(255,255,255,0.06)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                <Box sx={{ width: "90%", height: 4, borderRadius: 4, bgcolor: "rgba(255,255,255,0.12)" }} />
                <Box sx={{ width: "60%", height: 4, borderRadius: 4, bgcolor: "rgba(255,255,255,0.08)" }} />
              </Box>
            </Box>
          </Box>

          {/* Color Info */}
          <Box sx={{ px: 2.5, pb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                Primary Color
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: activePreset.primary }}>
                {activePreset.primary}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                Secondary Accent
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: activePreset.secondary || activePreset.primary }}>
                {activePreset.secondary || activePreset.primary}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
